import type { LlmUsageSummary } from "~/utils/llm-usage";
import { DBFactory } from "~/classes/database/db_factory";
import { type EvaluationMetric } from "~/types/EvaluationMetric";
import { BaseModel } from "../base.model";

export type EvaluationTestRuns = EvaluationTestRun[];

type EvaluationTestRunConstructorParams = {
  id?: string;
  label: string;
  description: string;
  createdAt: number;
  lastRun?: number;
  evaluationMetricId?: string;
  evaluationMetric?: EvaluationMetric;
  status?: EvaluationTestRunStatus;
  tags?: string[];
};

export class EvaluationTestRun extends BaseModel {
  id?: string;
  label: string;
  description: string;
  createdAt: number;
  lastRun?: number;
  evaluationMetricId?: string;
  evaluationMetric?: EvaluationMetric;
  status: EvaluationTestRunStatus;
  tags: string[];

  constructor(params: EvaluationTestRunConstructorParams) {
    super();
    this.id = params.id;
    this.label = params.label;
    this.description = params.description;
    this.createdAt = params.createdAt;
    this.lastRun = params.lastRun;
    this.evaluationMetricId = params.evaluationMetricId;
    this.evaluationMetric = params.evaluationMetric;
    this.status = params.status ?? EvaluationTestRunStatus.comparison;
    this.tags = params.tags ?? [];
  }

  override get databaseConfig(): ModelDatabaseConfig {
    return {
      path: `/evaluationTestRuns/${this.id}`,
      collection: "/evaluationTestRuns",
    };
  }

  static override fromMap(map: any): EvaluationTestRun {
    return new EvaluationTestRun(map);
  }

  async save() {
    const db = DBFactory.createDatabase();
    this.id = await db.save(this.toMap(), this.databaseConfig);
  }

  async delete() {
    const db = DBFactory.createDatabase();
    await db.delete(this.databaseConfig);
  }
}

type EvaluationTestRunResultsConstructorParams =
  EvaluationTestRunConstructorParams & {
    evaluationTestRunId: string;
    numTestsRun: number;
    numTestsPassed: number;
    numTestsFailed: number;
    totalInputTokens: number;
    totalOutputTokens: number;
    totalCost: number;
    llmAnalytics?: LlmUsageSummary;
    totalTestsWithLLMUsage: number;
    totalDuration: number;
    totalTestsWithDuration: number;
  };

export class EvaluationTestRunResults extends EvaluationTestRun {
  evaluationTestRunId: string;
  numTestsRun: number;
  numTestsPassed: number;
  numTestsFailed: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCost: number;
  llmAnalytics?: LlmUsageSummary;
  totalTestsWithLLMUsage: number;
  totalDuration: number;
  totalTestsWithDuration: number;

  constructor(params: EvaluationTestRunResultsConstructorParams) {
    super(params);

    this.evaluationTestRunId = params.evaluationTestRunId;
    this.numTestsRun = params.numTestsRun;
    this.numTestsPassed = params.numTestsPassed;
    this.numTestsFailed = params.numTestsFailed;
    this.totalInputTokens = params.totalInputTokens;
    this.totalOutputTokens = params.totalOutputTokens;
    this.totalCost = params.totalCost ?? 0;
    if (params.llmAnalytics) this.llmAnalytics = params.llmAnalytics;
    this.totalTestsWithLLMUsage = params.totalTestsWithLLMUsage;
    this.totalDuration = params.totalDuration;
    this.totalTestsWithDuration = params.totalTestsWithDuration;
  }

  static fromEvaluationTestRun({
    evaluationTestRun,
    numTestsRun,
    numTestsPassed,
    numTestsFailed,
    totalInputTokens,
    totalOutputTokens,
    totalTestsWithLLMUsage,
    totalDuration,
    totalTestsWithDuration,
    totalCost,
    llmAnalytics,
  }: {
    evaluationTestRun: EvaluationTestRun;
    numTestsRun: number;
    numTestsPassed: number;
    numTestsFailed: number;
    totalInputTokens: number;
    totalOutputTokens: number;
    totalCost: number;
    llmAnalytics?: LlmUsageSummary;
    totalTestsWithLLMUsage: number;
    totalDuration: number;
    totalTestsWithDuration: number;
  }): EvaluationTestRunResults {
    var evaluationTestRunId = evaluationTestRun.id;

    if (evaluationTestRunId == undefined) {
      throw new Error("Evaluation test run id is undefined");
    }

    const runCopy = { ...evaluationTestRun };
    delete runCopy.id;

    runCopy.createdAt = Date.now();

    return new EvaluationTestRunResults({
      ...runCopy,
      evaluationTestRunId,
      numTestsRun,
      numTestsPassed,
      numTestsFailed,
      totalInputTokens,
      totalOutputTokens,
      totalCost,
      ...(llmAnalytics ? { llmAnalytics } : {}),
      totalTestsWithLLMUsage,
      totalDuration,
      totalTestsWithDuration,
    });
  }

  override get databaseConfig(): ModelDatabaseConfig {
    return {
      path: `/evaluationTestRuns/${this.evaluationTestRunId}/results/${this.id}`,
      collection: `/evaluationTestRuns/${this.evaluationTestRunId}/results`,
    };
  }

  static override fromMap(map: any): EvaluationTestRunResults {
    return new EvaluationTestRunResults(map);
  }

  override async save() {
    const db = DBFactory.createDatabase();
    this.id = await db.save(this.toMap(), this.databaseConfig);
  }

  override async delete() {
    const db = DBFactory.createDatabase();
    await db.delete(this.databaseConfig);
  }
}
